export const FROZEN_DELIVERY_QUOTE_BOX_UNITS = 20;
export const FROZEN_DELIVERY_QUOTE_MAX_QUANTITY = 12;

export type FrozenDeliveryQuoteProductId =
  | "largeMeal"
  | "mediumMeal"
  | "smallPot"
  | "largePot"
  | "quarterCooler"
  | "halfCooler"
  | "fullCooler"
  | "side";

export type FrozenDeliveryQuoteProduct = {
  capacityLabel: string;
  customerDescription: string;
  fitHint: string;
  id: FrozenDeliveryQuoteProductId;
  imageAlt: string;
  imageSrc: string;
  name: string;
  short: string;
  note: string;
  units: number;
  color: string;
};

export type FrozenDeliveryQuoteQuantities = Record<
  FrozenDeliveryQuoteProductId,
  number
>;

export type FrozenDeliveryQuotePiece = FrozenDeliveryQuoteProduct & {
  name: string;
  short: string;
  productUnits: number;
};

export type FrozenDeliveryQuoteBox = {
  items: FrozenDeliveryQuotePiece[];
  remainingUnits: number;
  usedUnits: number;
  usedPercent: number;
  freePercent: number;
};

export type FrozenDeliveryQuotePresetKey = "one" | "two" | "cooler";

export type FrozenDeliveryQuoteSummary = {
  boxes: FrozenDeliveryQuoteBox[];
  pieces: FrozenDeliveryQuotePiece[];
  totalUnits: number;
  totalBoxShare: number;
  boxesNeeded: number;
  pricePerBox: number | null;
  formattedPricePerBox: string | null;
  deliveryCost: number | null;
  formattedDeliveryCost: string | null;
  capacityLine: string;
  spareLabel: string;
  hasItems: boolean;
  isExactFill: boolean;
  lastBoxRemainingUnits: number;
  lastBoxSparePercent: number;
};

export type FrozenDeliveryQuoteBoxTab = {
  box: FrozenDeliveryQuoteBox | null;
  boxIndex: number | null;
  isFull: boolean;
  isSyntheticEmpty: boolean;
  label: string;
};

export type FrozenDeliveryQuoteBoxTabState = {
  activeTabIndex: number;
  tabs: FrozenDeliveryQuoteBoxTab[];
};

export type FrozenDeliveryQuoteBoxPreviewLayoutViewport =
  | "desktop"
  | "mobile";

export type FrozenDeliveryQuotePreviewSliceLayout = {
  actualShare: number;
  heightRem: number;
  units: number;
  visualShare: number;
};

export type FrozenDeliveryQuoteBoxPreviewLayout = {
  boxVisualHeightRem: number;
  freeSpaceHeightRem: number;
  freeSpaceActualShare: number;
  freeSpaceVisualShare: number;
  slices: FrozenDeliveryQuotePreviewSliceLayout[];
};

export const FROZEN_DELIVERY_QUOTE_PRODUCTS: readonly FrozenDeliveryQuoteProduct[] =
  [
    {
      capacityLabel: "30% of the space",
      customerDescription: "A Large Meal takes up 30% of the space.",
      fitHint: "You can fit 3 Large Meals and 1 Side into one box.",
      id: "largeMeal",
      imageAlt: "Large frozen Nigerian meal tray",
      imageSrc: "/images/delivery-quote/largeMeal.png",
      name: "Large Meal",
      short: "Large meal",
      note: "A bigger tray for hearty portions and family-style ordering.",
      units: 6,
      color: "#1f9dff",
    },
    {
      capacityLabel: "20% of the space",
      customerDescription: "A Medium Meal takes up 20% of the space.",
      fitHint: "You can fit 5 Medium Meals into one box.",
      id: "mediumMeal",
      imageAlt: "Medium frozen Nigerian meal tray",
      imageSrc: "/images/delivery-quote/mediumMeal.png",
      name: "Medium Meal",
      short: "Medium meal",
      note: "A regular meal tray that stacks neatly into chilled parcels.",
      units: 4,
      color: "#57c7ff",
    },
    {
      capacityLabel: "40% of the space",
      customerDescription: "A Small Pot (3 liters) takes up 40% of the space.",
      fitHint: "You can fit 2 Small Pots and 2 Sides into one box.",
      id: "smallPot",
      imageAlt: "Small pot of frozen Nigerian soup",
      imageSrc: "/images/delivery-quote/smallpot.png",
      name: "Small Pot (3 liters)",
      short: "Small pot",
      note: "A compact soup or stew pot with a deeper frozen box share.",
      units: 8,
      color: "#6f7cff",
    },
    {
      capacityLabel: "50% of the space",
      customerDescription: "A Large Pot (5 liters) takes up 50% of the space.",
      fitHint: "You can fit 2 Large Pots into one box.",
      id: "largePot",
      imageAlt: "Large pot of frozen Nigerian food",
      imageSrc: "/images/delivery-quote/largepot.png",
      name: "Large Pot (5 liters)",
      short: "Large pot",
      note: "A bigger batch pot that quickly anchors a frozen parcel.",
      units: 10,
      color: "#2ec5b6",
    },
    {
      capacityLabel: "50% of the space",
      customerDescription: "A 1/4 Cooler (10 liters) takes up 50% of the space.",
      fitHint: "You can fit 2 quarter coolers into one box.",
      id: "quarterCooler",
      imageAlt: "Quarter cooler frozen food tubs",
      imageSrc: "/images/delivery-quote/quarterCooler.png",
      name: "1/4 Cooler (10 liters)",
      short: "1/4 cooler",
      note: "A cooler bundle that takes half of one courier box.",
      units: 10,
      color: "#4bc76f",
    },
    {
      capacityLabel: "100% of the space",
      customerDescription: "A 1/2 Cooler (10 liters) takes up 100% of the space.",
      fitHint: "One half cooler fills one box.",
      id: "halfCooler",
      imageAlt: "Half cooler frozen food tubs",
      imageSrc: "/images/delivery-quote/halfCooler.png",
      name: "1/2 Cooler (10 liters)",
      short: "1/2 cooler",
      note: "A half cooler travels as one full packed courier box.",
      units: 20,
      color: "#f2a93b",
    },
    {
      capacityLabel: "2 boxes",
      customerDescription: "A Full Cooler (20 liters) takes up 2 boxes.",
      fitHint: "One full cooler needs 2 boxes.",
      id: "fullCooler",
      imageAlt: "Full cooler frozen food tubs",
      imageSrc: "/images/delivery-quote/fullCooler.png",
      name: "Full Cooler (20 liters)",
      short: "Full cooler",
      note: "Full coolers split across two courier boxes automatically.",
      units: 40,
      color: "#f56f7a",
    },
    {
      capacityLabel: "10% of the space",
      customerDescription: "A Side takes up 10% of the space.",
      fitHint: "You can fit 10 Sides into one box.",
      id: "side",
      imageAlt: "Frozen side portion",
      imageSrc: "/images/delivery-quote/side.png",
      name: "Side",
      short: "Side",
      note: "Small add-ons that top up spare space before the next box starts.",
      units: 2,
      color: "#8ddf7b",
    },
  ] as const;

export const FROZEN_DELIVERY_QUOTE_PRESETS: Readonly<
  Record<FrozenDeliveryQuotePresetKey, FrozenDeliveryQuoteQuantities>
> = {
  one: {
    largeMeal: 0,
    mediumMeal: 1,
    smallPot: 0,
    largePot: 1,
    quarterCooler: 0,
    halfCooler: 0,
    fullCooler: 0,
    side: 2,
  },
  two: {
    largeMeal: 0,
    mediumMeal: 1,
    smallPot: 0,
    largePot: 1,
    quarterCooler: 0,
    halfCooler: 1,
    fullCooler: 0,
    side: 2,
  },
  cooler: {
    largeMeal: 0,
    mediumMeal: 0,
    smallPot: 0,
    largePot: 0,
    quarterCooler: 0,
    halfCooler: 0,
    fullCooler: 1,
    side: 0,
  },
} as const;

export const FROZEN_DELIVERY_QUOTE_BOX_PREVIEW_HEIGHT_REM = 26;
export const FROZEN_DELIVERY_QUOTE_BOX_PREVIEW_HEIGHT_REM_MOBILE = 21;
const FROZEN_DELIVERY_QUOTE_MINIMUM_VISUAL_SHARE = 0.19;
const FROZEN_DELIVERY_QUOTE_DEFAULT_SMOOTHING_STRENGTH = 0.23;

const currencyFormatter = new Intl.NumberFormat("en-GB", {
  currency: "GBP",
  maximumFractionDigits: 2,
  minimumFractionDigits: 0,
  style: "currency",
});

export function formatFrozenDeliveryQuoteCurrency(value: number) {
  return currencyFormatter.format(value);
}

export function boxShareForUnits(units: number) {
  const share = units / FROZEN_DELIVERY_QUOTE_BOX_UNITS;
  if (Number.isInteger(share)) {
    return `${share} ${share === 1 ? "box" : "boxes"}`;
  }

  return `${Number(share.toFixed(2))} box`;
}

export function capacityPercentForUnits(units: number) {
  return Math.round((units / FROZEN_DELIVERY_QUOTE_BOX_UNITS) * 100);
}

export function capacityLabelForUnits(units: number) {
  if (units > FROZEN_DELIVERY_QUOTE_BOX_UNITS) {
    return boxShareForUnits(units);
  }

  return `${capacityPercentForUnits(units)}% of the space`;
}

function formatFrozenDeliveryQuoteBoxCount(boxesNeeded: number) {
  return `${boxesNeeded} ${boxesNeeded === 1 ? "box" : "boxes"}`;
}

function buildFrozenDeliveryQuoteCapacityLine({
  boxes,
  boxesNeeded,
  hasItems,
  isExactFill,
  lastBoxSparePercent,
}: {
  boxes: FrozenDeliveryQuoteBox[];
  boxesNeeded: number;
  hasItems: boolean;
  isExactFill: boolean;
  lastBoxSparePercent: number;
}) {
  if (!hasItems) {
    return "Add products to see how much box space your order needs.";
  }

  const formattedBoxCount = formatFrozenDeliveryQuoteBoxCount(boxesNeeded);

  if (isExactFill) {
    return `These items fill ${formattedBoxCount} exactly.`;
  }

  const currentBoxNumber = Math.max(1, boxes.length);

  return `These items take up ${formattedBoxCount}. You still have ${lastBoxSparePercent}% of space left in Box ${currentBoxNumber}.`;
}

function roundFrozenDeliveryQuotePreviewRem(value: number) {
  return Number(value.toFixed(2));
}

function roundFrozenDeliveryQuotePreviewShare(value: number) {
  return Number(value.toFixed(4));
}

type FrozenDeliveryQuotePreviewSegment = {
  actualShare: number;
  units: number;
};

type FrozenDeliveryQuotePreviewConfig = {
  minimumReadableShare?: number;
  smoothingStrength?: number;
};

function minimumFrozenDeliveryQuoteSliceHeightRem(
  units: number,
  viewport: FrozenDeliveryQuoteBoxPreviewLayoutViewport,
) {
  if (viewport === "mobile") {
    if (units >= 20) {
      return 4.4;
    }

    if (units >= 10) {
      return 3.8;
    }

    if (units >= 8) {
      return 3.5;
    }

    if (units >= 6) {
      return 3.2;
    }

    if (units >= 4) {
      return 2.9;
    }

    return 2.45;
  }

  if (units >= 20) {
    return 4.9;
  }

  if (units >= 10) {
    return 4.15;
  }

  if (units >= 8) {
    return 3.75;
  }

  if (units >= 6) {
    return 3.45;
  }

  if (units >= 4) {
    return 3.15;
  }

  return 2.65;
}

function resolveFrozenDeliveryQuotePreviewConfig(
  config: FrozenDeliveryQuotePreviewConfig = {},
) {
  return {
    minimumReadableShare:
      config.minimumReadableShare ?? FROZEN_DELIVERY_QUOTE_MINIMUM_VISUAL_SHARE,
    smoothingStrength:
      config.smoothingStrength ??
      FROZEN_DELIVERY_QUOTE_DEFAULT_SMOOTHING_STRENGTH,
  };
}

function rebalanceFrozenDeliveryQuotePreviewSegments(
  segments: readonly FrozenDeliveryQuotePreviewSegment[],
  config: FrozenDeliveryQuotePreviewConfig = {},
) {
  if (segments.length <= 1) {
    return segments.map((segment) => segment.actualShare);
  }

  const resolvedConfig = resolveFrozenDeliveryQuotePreviewConfig(config);
  const targetShare = Math.min(
    resolvedConfig.minimumReadableShare,
    1 / segments.length,
  );
  const requestedLiftByIndex = segments.map((segment) =>
    Math.max(0, targetShare - segment.actualShare) *
      resolvedConfig.smoothingStrength,
  );
  const donorCapacityByIndex = segments.map((segment) =>
    Math.max(0, segment.actualShare - targetShare),
  );
  const totalDonorCapacity = donorCapacityByIndex.reduce(
    (total, capacity) => total + capacity,
    0,
  );
  const totalRequestedLift = requestedLiftByIndex.reduce(
    (total, lift) => total + lift,
    0,
  );

  if (totalDonorCapacity <= 0 || totalRequestedLift <= 0) {
    return segments.map((segment) => segment.actualShare);
  }

  const liftScalingRatio = Math.min(1, totalDonorCapacity / totalRequestedLift);
  const appliedLiftByIndex = requestedLiftByIndex.map(
    (lift) => lift * liftScalingRatio,
  );
  const totalAppliedLift = appliedLiftByIndex.reduce(
    (total, lift) => total + lift,
    0,
  );
  const donorTransferRatio = Math.min(
    1,
    totalAppliedLift / totalDonorCapacity,
  );
  const visualShares = segments.map((segment, index) => {
    const appliedLift = appliedLiftByIndex[index];
    if (appliedLift > 0) {
      return segment.actualShare + appliedLift;
    }

    const donorCapacity = donorCapacityByIndex[index];
    if (donorCapacity <= 0) {
      return segment.actualShare;
    }

    return segment.actualShare - donorCapacity * donorTransferRatio;
  });
  const totalVisualShare = visualShares.reduce(
    (total, share) => total + share,
    0,
  );
  const normalizationDelta = 1 - totalVisualShare;

  if (Math.abs(normalizationDelta) > 0.0000001) {
    const targetIndex =
      visualShares.findLastIndex((share) => share > 0) >= 0
        ? visualShares.findLastIndex((share) => share > 0)
        : visualShares.length - 1;

    visualShares[targetIndex] += normalizationDelta;
  }

  return visualShares;
}

export function buildFrozenDeliveryQuoteBoxPreviewLayout(
  box: FrozenDeliveryQuoteBox,
  viewport: FrozenDeliveryQuoteBoxPreviewLayoutViewport = "desktop",
  config: FrozenDeliveryQuotePreviewConfig = {},
): FrozenDeliveryQuoteBoxPreviewLayout {
  const baseBoxHeightRem =
    viewport === "mobile"
      ? FROZEN_DELIVERY_QUOTE_BOX_PREVIEW_HEIGHT_REM_MOBILE
      : FROZEN_DELIVERY_QUOTE_BOX_PREVIEW_HEIGHT_REM;
  const freeSpaceActualShare =
    box.remainingUnits / FROZEN_DELIVERY_QUOTE_BOX_UNITS;
  const segments: FrozenDeliveryQuotePreviewSegment[] = [
    ...box.items.map((item) => ({
      actualShare: item.units / FROZEN_DELIVERY_QUOTE_BOX_UNITS,
      units: item.units,
    })),
    ...(freeSpaceActualShare > 0
      ? [
          {
            actualShare: freeSpaceActualShare,
            units: box.remainingUnits,
          },
        ]
      : []),
  ];
  const visualShares = rebalanceFrozenDeliveryQuotePreviewSegments(
    segments,
    config,
  );
  const slices = box.items.map((item, index) => ({
    actualShare: item.units / FROZEN_DELIVERY_QUOTE_BOX_UNITS,
    units: item.units,
    visualShare: visualShares[index] ?? item.units / FROZEN_DELIVERY_QUOTE_BOX_UNITS,
  }));
  const boxVisualHeightRem = roundFrozenDeliveryQuotePreviewRem(
    slices.reduce((currentHeight, slice) => {
      if (slice.visualShare <= 0) {
        return currentHeight;
      }

      return Math.max(
        currentHeight,
        minimumFrozenDeliveryQuoteSliceHeightRem(slice.units, viewport) /
          slice.visualShare,
      );
    }, baseBoxHeightRem),
  );
  const freeSpaceVisualShare = box.remainingUnits > 0
    ? (visualShares[visualShares.length - 1] ?? freeSpaceActualShare)
    : 0;

  return {
    boxVisualHeightRem,
    freeSpaceHeightRem: roundFrozenDeliveryQuotePreviewRem(
      boxVisualHeightRem * freeSpaceVisualShare,
    ),
    freeSpaceActualShare: roundFrozenDeliveryQuotePreviewShare(
      freeSpaceActualShare,
    ),
    freeSpaceVisualShare: roundFrozenDeliveryQuotePreviewShare(
      freeSpaceVisualShare,
    ),
    slices: slices.map((slice) => ({
      actualShare: roundFrozenDeliveryQuotePreviewShare(slice.actualShare),
      heightRem: roundFrozenDeliveryQuotePreviewRem(
        boxVisualHeightRem * slice.visualShare,
      ),
      units: slice.units,
      visualShare: roundFrozenDeliveryQuotePreviewShare(slice.visualShare),
    })),
  };
}

export function createFrozenDeliveryQuoteEmptyQuantities(): FrozenDeliveryQuoteQuantities {
  return Object.fromEntries(
    FROZEN_DELIVERY_QUOTE_PRODUCTS.map((product) => [product.id, 0]),
  ) as FrozenDeliveryQuoteQuantities;
}

export function cloneFrozenDeliveryQuotePreset(
  key: FrozenDeliveryQuotePresetKey,
): FrozenDeliveryQuoteQuantities {
  return {
    ...FROZEN_DELIVERY_QUOTE_PRESETS[key],
  };
}

export function quantitiesMatchFrozenDeliveryQuotePreset(
  quantities: FrozenDeliveryQuoteQuantities,
  presetKey: FrozenDeliveryQuotePresetKey,
) {
  const preset = FROZEN_DELIVERY_QUOTE_PRESETS[presetKey];

  return FROZEN_DELIVERY_QUOTE_PRODUCTS.every(
    (product) => (quantities[product.id] ?? 0) === preset[product.id],
  );
}

export function updateFrozenDeliveryQuoteQuantity(
  quantities: FrozenDeliveryQuoteQuantities,
  productId: FrozenDeliveryQuoteProductId,
  delta: number,
): FrozenDeliveryQuoteQuantities {
  return {
    ...quantities,
    [productId]: clampFrozenDeliveryQuoteQuantity(
      (quantities[productId] ?? 0) + delta,
    ),
  };
}

export function clampFrozenDeliveryQuoteQuantity(value: number) {
  return Math.max(
    0,
    Math.min(FROZEN_DELIVERY_QUOTE_MAX_QUANTITY, Math.trunc(value)),
  );
}

export function getFrozenDeliveryQuotePieces(
  quantities: FrozenDeliveryQuoteQuantities,
) {
  return FROZEN_DELIVERY_QUOTE_PRODUCTS.flatMap((product) => {
    const pieces: FrozenDeliveryQuotePiece[] = [];
    const count = quantities[product.id] ?? 0;

    for (let itemIndex = 0; itemIndex < count; itemIndex += 1) {
      let remainingUnits = product.units;
      let part = 1;
      const totalParts = Math.ceil(
        product.units / FROZEN_DELIVERY_QUOTE_BOX_UNITS,
      );

      while (remainingUnits > 0) {
        const pieceUnits = Math.min(
          remainingUnits,
          FROZEN_DELIVERY_QUOTE_BOX_UNITS,
        );
        const splitLabel = product.units > FROZEN_DELIVERY_QUOTE_BOX_UNITS
          ? ` ${part}/${totalParts}`
          : "";

        pieces.push({
          ...product,
          name:
            product.units > FROZEN_DELIVERY_QUOTE_BOX_UNITS
              ? `${product.name} box ${part} of ${totalParts}`
              : product.name,
          short: `${product.short}${splitLabel}`,
          units: pieceUnits,
          productUnits: product.units,
        });

        remainingUnits -= pieceUnits;
        part += 1;
      }
    }

    return pieces;
  }).sort((left, right) => right.units - left.units);
}

export function packFrozenDeliveryQuoteBoxes(
  pieces: readonly FrozenDeliveryQuotePiece[],
) {
  const boxes: Array<{
    items: FrozenDeliveryQuotePiece[];
    remainingUnits: number;
  }> = [];

  for (const piece of pieces) {
    let target = boxes.find((box) => box.remainingUnits >= piece.units);
    if (!target) {
      target = {
        items: [],
        remainingUnits: FROZEN_DELIVERY_QUOTE_BOX_UNITS,
      };
      boxes.push(target);
    }

    target.items.push(piece);
    target.remainingUnits -= piece.units;
  }

  return boxes.map((box) => {
    const usedUnits = FROZEN_DELIVERY_QUOTE_BOX_UNITS - box.remainingUnits;

    return {
      items: box.items,
      remainingUnits: box.remainingUnits,
      usedUnits,
      usedPercent: Math.round(
        (usedUnits / FROZEN_DELIVERY_QUOTE_BOX_UNITS) * 100,
      ),
      freePercent: Math.round(
        (box.remainingUnits / FROZEN_DELIVERY_QUOTE_BOX_UNITS) * 100,
      ),
    };
  });
}

export function buildFrozenDeliveryQuoteSummary(
  quantities: FrozenDeliveryQuoteQuantities,
  pricePerBox: number | null | undefined,
): FrozenDeliveryQuoteSummary {
  const pieces = getFrozenDeliveryQuotePieces(quantities);
  const boxes = packFrozenDeliveryQuoteBoxes(pieces);
  const normalizedPricePerBox =
    typeof pricePerBox === "number" &&
    Number.isFinite(pricePerBox) &&
    pricePerBox > 0
      ? pricePerBox
      : null;
  const totalUnits = pieces.reduce((total, piece) => total + piece.units, 0);
  const boxesNeeded = boxes.length;
  const totalBoxShare = totalUnits / FROZEN_DELIVERY_QUOTE_BOX_UNITS;
  const lastBoxRemainingUnits =
    boxes[boxes.length - 1]?.remainingUnits ?? FROZEN_DELIVERY_QUOTE_BOX_UNITS;
  const lastBoxSparePercent = Math.round(
    (lastBoxRemainingUnits / FROZEN_DELIVERY_QUOTE_BOX_UNITS) * 100,
  );
  const isExactFill = pieces.length > 0 && lastBoxRemainingUnits === 0;
  const deliveryCost =
    normalizedPricePerBox === null ? null : boxesNeeded * normalizedPricePerBox;
  const formattedPricePerBox =
    normalizedPricePerBox === null
      ? null
      : formatFrozenDeliveryQuoteCurrency(normalizedPricePerBox);
  const formattedDeliveryCost =
    deliveryCost === null
      ? null
      : formatFrozenDeliveryQuoteCurrency(deliveryCost);

  if (pieces.length === 0) {
    return {
      boxes,
      pieces,
      totalUnits,
      totalBoxShare,
      boxesNeeded,
      pricePerBox: normalizedPricePerBox,
      formattedPricePerBox,
      deliveryCost,
      formattedDeliveryCost,
      capacityLine: buildFrozenDeliveryQuoteCapacityLine({
        boxes,
        boxesNeeded,
        hasItems: false,
        isExactFill: false,
        lastBoxSparePercent,
      }),
      spareLabel: "No box yet",
      hasItems: false,
      isExactFill: false,
      lastBoxRemainingUnits,
      lastBoxSparePercent,
    };
  }

  if (isExactFill) {
    return {
      boxes,
      pieces,
      totalUnits,
      totalBoxShare,
      boxesNeeded,
      pricePerBox: normalizedPricePerBox,
      formattedPricePerBox,
      deliveryCost,
      formattedDeliveryCost,
      capacityLine: buildFrozenDeliveryQuoteCapacityLine({
        boxes,
        boxesNeeded,
        hasItems: true,
        isExactFill: true,
        lastBoxSparePercent,
      }),
      spareLabel: "Box full",
      hasItems: true,
      isExactFill: true,
      lastBoxRemainingUnits,
      lastBoxSparePercent,
    };
  }

  return {
    boxes,
    pieces,
    totalUnits,
    totalBoxShare,
    boxesNeeded,
    pricePerBox: normalizedPricePerBox,
    formattedPricePerBox,
    deliveryCost,
    formattedDeliveryCost,
    capacityLine: buildFrozenDeliveryQuoteCapacityLine({
      boxes,
      boxesNeeded,
      hasItems: true,
      isExactFill: false,
      lastBoxSparePercent,
    }),
    spareLabel: `${lastBoxSparePercent}% space left`,
    hasItems: true,
    isExactFill: false,
    lastBoxRemainingUnits,
    lastBoxSparePercent,
  };
}

export function getFrozenDeliveryQuoteBoxTabState(
  summary: FrozenDeliveryQuoteSummary,
): FrozenDeliveryQuoteBoxTabState {
  if (!summary.hasItems || summary.boxes.length === 0) {
    return {
      activeTabIndex: 0,
      tabs: [
        {
          box: null,
          boxIndex: null,
          isFull: false,
          isSyntheticEmpty: true,
          label: "Box 1",
        },
      ],
    };
  }

  const tabs = summary.boxes.map((box, index) => ({
    box,
    boxIndex: index,
    isFull: box.usedPercent >= 100,
    isSyntheticEmpty: false,
    label: box.usedPercent >= 100 ? `Box ${index + 1} Full` : `Box ${index + 1}`,
  }));
  const lastIncompleteBoxIndex = summary.boxes.reduce(
    (lastIndex, box, index) => (box.usedPercent < 100 ? index : lastIndex),
    -1,
  );

  if (lastIncompleteBoxIndex >= 0) {
    return {
      activeTabIndex: lastIncompleteBoxIndex,
      tabs,
    };
  }

  return {
    activeTabIndex: Math.max(0, tabs.length - 1),
    tabs,
  };
}
